/**
 * Settings API — RTK Query endpoints for Settings feature
 *
 * All settings-related data access goes through these endpoints.
 * Zero direct supabase.* calls in components.
 *
 * Endpoints:
 *   - getSettingsInitialData  → fn_settings_get_initial_data RPC
 *   - getSchoolLogoSignedUrl  → supabase.storage.createSignedUrl (queryFn)
 *   - saveSchoolInfo          → save_school_information_react RPC
 *   - uploadSchoolLogo        → supabase.storage.upload (queryFn)
 *   - deleteSchoolLogo        → supabase.storage.remove (queryFn)
 */

import { rootApi } from '../../../lib/api/rootApi';
import { supabase } from '@flexwise/shared/supabase';
import { teacherDashboardApi } from '../../teacherDashboard/api/teacherDashboardApi';

// ── Types ──────────────────────────────────────────────────────

export interface SchoolInfo {
  name: string;
  leadership: string;
  address: string;
  email: string;
  phone: string;
  fax: string;
  signature: string;
  logo_url: string | null;
  school_id?: string;
  school_number: string | null;
  header_logo_url: string | null;
}

export interface AdminStaffMember {
  id: string;
  name: string;
}

export interface SettingsInitialData {
  school_info: SchoolInfo;
  admin_staff: AdminStaffMember[];
}

export interface SaveSchoolInfoArgs {
  name: string;
  leadership: string;
  address: string;
  email: string;
  phone: string;
  fax: string;
  signature: string;
  logo_url: string | null;
  school_number: string | null;
  header_logo_url: string | null;
}

// ── API Slice ──────────────────────────────────────────────────

export const settingsApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Initial load: school info + admin staff in one RPC call.
     * Replaces: fetchSchoolInformation() + user_profiles query + roles query
     */
    getSettingsInitialData: builder.query<SettingsInitialData, void>({
      query: () => ({
        type: 'rpc',
        rpc: 'fn_settings_get_initial_data',
        params: {},
      }),
      providesTags: ['Settings'],
    }),

    /**
     * Generate a fresh signed URL for the school logo.
     * Uses queryFn for storage API access (approved pattern per RTK_QUERY_GUARDRAILS).
     */
    getSchoolLogoSignedUrl: builder.query<string | null, { logoPath: string | null }>({
      async queryFn({ logoPath }) {
        try {
          if (!logoPath) return { data: null };

          // Resolve file path from various URL formats
          let filePath = logoPath;

          if (logoPath.includes('/object/sign/school-logo/')) {
            const urlParts = logoPath.split('/object/sign/school-logo/');
            if (urlParts.length >= 2) filePath = decodeURIComponent(urlParts[1].split('?')[0]);
          } else if (logoPath.includes('/object/public/school-logo/')) {
            const urlParts = logoPath.split('/object/public/school-logo/');
            if (urlParts.length >= 2) filePath = decodeURIComponent(urlParts[1].split('?')[0]);
          } else if (logoPath.includes('/school-logo/')) {
            const urlParts = logoPath.split('/school-logo/');
            if (urlParts.length >= 2) filePath = decodeURIComponent(urlParts[1].split('?')[0]);
          }

          filePath = filePath.split('?')[0].split('#')[0];

          // Handle URLs that are still full HTTP paths
          if (/^https?:\/\//.test(filePath)) {
            const m = filePath.match(/\/school-logo\/(.+)$/);
            if (m?.[1]) filePath = decodeURIComponent(m[1]);
          }

          // Build candidate paths
          const candidates: string[] = [];

          if (/.+\/logos\//.test(filePath)) {
            candidates.push(filePath);
          } else if (filePath.startsWith('logos/')) {
            // Prefix with school_id if needed
            const { data: schoolId, error: schoolIdErr } = await supabase.rpc('get_user_school_id');
            if (!schoolIdErr && schoolId) {
              candidates.push(`${String(schoolId)}/${filePath}`);
            }
            candidates.push(filePath);
          } else {
            candidates.push(filePath);
          }

          // Try each candidate
          for (const candidate of candidates) {
            const { data, error } = await supabase.storage
              .from('school-logo')
              .createSignedUrl(candidate, 60 * 60);
            if (!error && data?.signedUrl) {
              return { data: data.signedUrl };
            }
          }

          // Fallback: public URL
          const fallbackPath = candidates[0];
          const { data: { publicUrl } } = supabase.storage
            .from('school-logo')
            .getPublicUrl(fallbackPath);
          if (publicUrl) return { data: publicUrl };

          return { data: null };
        } catch (error: any) {
          return { error: { message: error?.message || 'Failed to get signed URL', details: null, hint: null, code: 'storage_error' } };
        }
      },
      providesTags: ['Settings'],
    }),

    /**
     * Save school information via existing RPC.
     * Replaces: saveSchoolInformation()
     */
    saveSchoolInfo: builder.mutation<any, SaveSchoolInfoArgs>({
      query: (params) => ({
        type: 'rpc',
        rpc: 'save_school_information_react',
        params: {
          p_name: params.name,
          p_leadership: params.leadership,
          p_address: params.address,
          p_email: params.email,
          p_phone: params.phone,
          p_fax: params.fax,
          p_signature: params.signature,
          p_logo_url: params.logo_url || null,
          p_school_number: params.school_number ?? null,
          p_header_logo_url: params.header_logo_url ?? null,
        },
      }),
      invalidatesTags: ['Settings'],
    }),

    /**
     * Upload school logo to storage.
     * Uses queryFn for storage API access.
     */
    uploadSchoolLogo: builder.mutation<{ success: boolean; logo_url: string; file_path: string }, File>({
      async queryFn(file) {
        try {
          const fileExt = file.name.split('.').pop();
          const fileName = `school-logo-${Date.now()}.${fileExt}`;

          // Get school_id via RPC
          const { data: schoolData, error: schoolError } = await supabase.rpc('get_user_school_id');
          if (schoolError || !schoolData) {
            return { error: { message: 'Could not determine user school', details: null, hint: null, code: 'school_error' } };
          }

          const schoolId = String(schoolData);
          const filePath = `${schoolId}/logos/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('school-logo')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false,
              metadata: { school_id: schoolId },
            });

          if (uploadError) {
            return { error: { message: uploadError.message, details: null, hint: null, code: 'upload_error' } };
          }

          return {
            data: {
              success: true,
              logo_url: filePath,
              file_path: filePath,
            },
          };
        } catch (error: any) {
          return { error: { message: error?.message || 'Upload failed', details: null, hint: null, code: 'upload_error' } };
        }
      },
      invalidatesTags: ['Settings'],
    }),

    /**
     * Delete school logo from storage.
     * Uses queryFn for storage API access.
     */
    deleteSchoolLogo: builder.mutation<{ success: boolean }, { logoPath: string }>({
      async queryFn({ logoPath }) {
        try {
          if (!logoPath) return { data: { success: true } };

          // Resolve to a clean storage path
          let filePath = logoPath;
          if (logoPath.includes('/school-logo/')) {
            const urlParts = logoPath.split('/school-logo/');
            if (urlParts.length >= 2) {
              filePath = decodeURIComponent(urlParts[1].split('?')[0]);
            }
          }

          const { error } = await supabase.storage
            .from('school-logo')
            .remove([filePath]);

          if (error) {
            return { error: { message: error.message, details: null, hint: null, code: 'delete_error' } };
          }

          return { data: { success: true } };
        } catch (error: any) {
          return { error: { message: error?.message || 'Delete failed', details: null, hint: null, code: 'delete_error' } };
        }
      },
      invalidatesTags: ['Settings'],
    }),

    /**
     * Per-school per-module feature toggles. The JSONB shape is documented
     * in 20260517_school_module_settings.sql; consumers are free to read any
     * nested key and treat missing keys as `false`.
     */
    getSchoolModuleSettings: builder.query<Record<string, Record<string, boolean>>, void>({
      // PERF: prefer the cached teacher-dashboard bundle (which now ships
      // module_settings inline). Falls back to the dedicated RPC when the
      // dashboard hasn't loaded (e.g. a page that doesn't use it, or fresh
      // page-load order race). Realtime invalidation below still keeps both
      // paths fresh.
      async queryFn(_arg, { getState, dispatch }) {
        const getDashboardEntry = () => {
          const state = getState() as any;
          const qs = state.teacherDashboardApi?.queries;
          if (!qs) return null;
          return (
            (Object.values(qs).find(
              (q: any) =>
                q?.endpointName === 'getTeacherDashboardInitialLoad',
            ) as any) ?? null
          );
        };

        // 1. Fulfilled cache? done.
        let entry = getDashboardEntry();
        if (entry?.status === 'fulfilled') {
          const m = entry?.data?.moduleSettings;
          if (m && typeof m === 'object') {
            return { data: m as Record<string, Record<string, boolean>> };
          }
        }

        // 2. Dashboard might not even have been dispatched yet because this
        // settings hook runs in a child component that mounted before the
        // dashboard's parent. Wait up to 300 ms for it to appear in state.
        if (!entry) {
          for (let i = 0; i < 6; i++) {
            await new Promise((r) => setTimeout(r, 50));
            entry = getDashboardEntry();
            if (entry) break;
          }
        }

        // 3. Dashboard query exists (pending or fulfilled). Await it via
        // initiate(...).subscribe:false — reuses the existing in-flight
        // query and gets its data without firing another network call.
        if (entry && entry.status !== 'rejected' && entry.originalArgs) {
          try {
            const promise = dispatch(
              teacherDashboardApi.endpoints.getTeacherDashboardInitialLoad.initiate(
                entry.originalArgs,
                { subscribe: false, forceRefetch: false },
              ),
            );
            const result = await promise;
            const ms = (result as any)?.data?.moduleSettings;
            if (ms && typeof ms === 'object') {
              return { data: ms as Record<string, Record<string, boolean>> };
            }
          } catch {
            // fall through
          }
        }

        // 4. Truly non-teacher route (no dashboard query at all): RPC.
        const { data, error } = await supabase.rpc('fn_get_school_module_settings');
        if (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: (error as any).message || 'rpc_error',
            } as any,
          };
        }
        return {
          data:
            (data as Record<string, Record<string, boolean>> | null) ?? {},
        };
      },
      providesTags: ['Settings'],
      // Live invalidation across sessions. When the admin saves in one
      // tab, every other tab/user subscribed here sees the postgres_changes
      // event for school_module_settings and re-fetches. Replaces the
      // previous "teacher must refresh" behaviour. The supabase_realtime
      // publication for this table is enabled in
      // 20260517_school_module_settings_realtime.sql.
      async onCacheEntryAdded(_arg, { dispatch, cacheDataLoaded, cacheEntryRemoved }) {
        await cacheDataLoaded;
        const channel = supabase
          .channel('school_module_settings_changes')
          .on(
            'postgres_changes' as any,
            { event: '*', schema: 'public', table: 'school_module_settings' },
            () => {
              dispatch(rootApi.util.invalidateTags(['Settings']));
            },
          )
          .subscribe();
        try {
          await cacheEntryRemoved;
        } finally {
          try {
            supabase.removeChannel(channel);
          } catch {
            /* no-op */
          }
        }
      },
    }),

    setSchoolModuleSettings: builder.mutation<
      {
        settings: Record<string, Record<string, boolean>>;
        /** Number of future class_absences auto-converted from ausfall →
         *  sonstiges because the school turned the Ausfall flag off in
         *  the same save. Always present; 0 when nothing changed. */
        converted_future_ausfall: number;
      },
      { settings: Record<string, Record<string, boolean>> }
    >({
      query: ({ settings }) => ({
        type: 'rpc',
        rpc: 'fn_set_school_module_settings',
        params: { p_settings: settings },
      }),
      invalidatesTags: ['Settings', 'Substitution'],
    }),
  }),
});

// ── Exported Hooks ─────────────────────────────────────────────

export const {
  useGetSettingsInitialDataQuery,
  useGetSchoolLogoSignedUrlQuery,
  useSaveSchoolInfoMutation,
  useUploadSchoolLogoMutation,
  useDeleteSchoolLogoMutation,
  useGetSchoolModuleSettingsQuery,
  useSetSchoolModuleSettingsMutation,
} = settingsApi;
